import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, api, inr, fdate } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, ArrowDownCircle, ArrowUpCircle, Download, Trash2, ArrowLeftRight, Mail, Printer, Pencil } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque"];
const EMPTY = { kind: "receipt", party_id: "", amount: "", date: "", payment_mode: "", account_id: "", note: "" };

export default function Payments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const parties = form.kind === "receipt" ? clients : vendors;
  const partyKey = form.kind === "receipt" ? "client_id" : "vendor_id";

  const receiptsTotal = payments.filter((p) => p.kind === "receipt").reduce((s, p) => s + p.amount, 0);
  const vouchersTotal = payments.filter((p) => p.kind === "voucher").reduce((s, p) => s + p.amount, 0);

  const setKind = (kind) => setForm({ ...form, kind, party_id: "" });

  const openEdit = (p) => {
    setEditId(p.payment_id);
    setForm({ kind: p.kind, party_id: p.party_id, amount: p.amount, date: p.date, payment_mode: p.payment_mode, account_id: p.account_id, note: p.note || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.party_id) return toast.error(`Select a ${form.kind === "receipt" ? "client" : "vendor"}`);
    if (!form.account_id) return toast.error("Select a bank / cash account");
    if (!form.payment_mode) return toast.error("Select a payment mode");
    if (Number(form.amount) <= 0) return toast.error("Amount must be positive");
    setBusy(true);
    try {
      if (editId) {
        const r = await api.put(`/payments/${editId}`, {
          amount: Number(form.amount), account_id: form.account_id,
          payment_mode: form.payment_mode, date: form.date || undefined, note: form.note,
        });
        toast.success(`${r.data.number} updated — ledger, bank balance & invoice dues recalculated`);
      } else {
        const r = await api.post("/payments", {
          ...form, amount: Number(form.amount), date: form.date || undefined,
        });
        toast.success(`${r.data.number} saved — ledger, bank balance & Income/Expense updated`);
      }
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save payment");
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.number} (${inr(p.amount)})? The party ledger and bank balance will be reversed.`)) return;
    try {
      await api.delete(`/payments/${p.payment_id}`);
      toast.success(`${p.number} deleted — ledger & bank balance reversed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const emailDoc = async (p) => {
    try {
      const r = await api.post(`/payments/${p.payment_id}/email`);
      toast.success(`${r.data.number} emailed to ${r.data.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed");
    }
  };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments" sub="Receipts (inward from clients) & Vouchers (outward to vendors) — auto-posts to party ledger, bank balance and Income & Expense">
        <Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-payment-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> New Payment
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-6 mb-6" data-testid="payments-summary">
        <span className="font-mono2 text-sm text-slate-600">
          Receipts (In): <span className="text-emerald-600 font-bold" data-testid="payments-receipts-total">{inr(receiptsTotal)}</span>
        </span>
        <span className="font-mono2 text-sm text-slate-600">
          Vouchers (Out): <span className="text-red-600 font-bold" data-testid="payments-vouchers-total">{inr(vouchersTotal)}</span>
        </span>
      </div>

      {payments.length === 0 ? (
        <EmptyState icon={ArrowLeftRight} title="No payments yet" sub="Record a client receipt or a vendor payment voucher" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="payments-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Number</th><th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Party</th><th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Account</th><th className="px-5 py-3">Note</th>
                <th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {payments.map((p) => (
                <tr key={p.payment_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`payment-row-${p.payment_id}`}>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-mono2 font-semibold ${p.kind === "receipt" ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-red-500/40 text-red-600 bg-red-500/10"}`}
                      data-testid={`payment-number-${p.payment_id}`}>
                      {p.number}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(p.date)}</td>
                  <td className="px-5 py-3.5 font-medium text-slate-900">
                    {p.party_name}
                    {p.allocations?.length > 0 && (
                      <p className="text-[10px] font-mono2 text-emerald-600 mt-0.5" data-testid={`payment-allocations-${p.payment_id}`}>
                        Adjusted: {p.allocations.map((a) => a.number).join(", ")}{p.on_account > 0 ? " + on-account" : ""}
                      </p>
                    )}
                    {(!p.allocations || p.allocations.length === 0) && p.kind === "receipt" && p.on_account > 0 && (
                      <p className="text-[10px] font-mono2 text-blue-600 mt-0.5" data-testid={`payment-allocations-${p.payment_id}`}>Advance on account</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {p.payment_mode
                      ? <span className="px-2 py-0.5 rounded-full border border-blue-500/40 text-blue-600 bg-blue-500/10 text-[11px] font-medium">{p.payment_mode}</span>
                      : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{p.account_name}</td>
                  <td className="px-5 py-3.5 text-slate-500">{p.note || "—"}</td>
                  <td className={`px-5 py-3.5 text-right font-mono2 font-bold ${p.kind === "receipt" ? "text-emerald-600" : "text-red-600"}`}>
                    {p.kind === "receipt" ? "+" : "−"}{inr(p.amount)}
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Link to={`/payment/${p.payment_id}/print`} data-testid={`payment-view-${p.payment_id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors duration-150">
                      <Printer className="w-3.5 h-3.5" /> View / Print
                    </Link>
                    <a href={`${API}/payments/${p.payment_id}/pdf`} download data-testid={`payment-pdf-${p.payment_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                    <button onClick={() => openEdit(p)} data-testid={`payment-edit-${p.payment_id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors duration-150">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => emailDoc(p)} data-testid={`payment-email-${p.payment_id}`}
                      title={p.party_email ? `Email to ${p.party_email}` : "Party has no email on record"}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-500/10 transition-colors duration-150">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(p)} data-testid={`payment-delete-${p.payment_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditId(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="add-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {form.kind === "receipt"
                ? <ArrowDownCircle className="w-5 h-5 text-emerald-600" />
                : <ArrowUpCircle className="w-5 h-5 text-red-600" />}
              {editId ? "Edit" : ""} {form.kind === "receipt" ? "Payment Receipt (Inward)" : "Payment Voucher (Outward)"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="flex rounded-full border border-slate-300 overflow-hidden" data-testid="payment-kind-tabs">
              <button type="button" onClick={() => setKind("receipt")} disabled={!!editId} data-testid="payment-kind-receipt"
                className={`flex-1 px-4 py-2 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${form.kind === "receipt" ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
                Receipt — From Client
              </button>
              <button type="button" onClick={() => setKind("voucher")} disabled={!!editId} data-testid="payment-kind-voucher"
                className={`flex-1 px-4 py-2 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${form.kind === "voucher" ? "bg-red-500 text-white" : "text-slate-600 hover:text-slate-800"}`}>
                Voucher — To Vendor
              </button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{form.kind === "receipt" ? "Received From (Client) *" : "Paid To (Vendor) *"}</Label>
              <select value={form.party_id} onChange={(e) => setForm({ ...form, party_id: e.target.value })} disabled={!!editId} data-testid="payment-party-select" className={`${selCls} disabled:opacity-60`}>
                <option value="">Select {form.kind === "receipt" ? "client" : "vendor"}…</option>
                {parties.map((p) => <option key={p[partyKey]} value={p[partyKey]}>{p.name}</option>)}
              </select>
              {editId && <p className="text-[11px] text-slate-500">Party & type are locked when editing — delete and re-create to change them</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Amount (₹) *</Label>
                <Input type="number" step="0.01" min="0" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{form.kind === "receipt" ? "Deposit Into Account *" : "Pay From Account *"}</Label>
              <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="payment-account-select" className={selCls}>
                <option value="">Select account…</option>
                {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name} — {inr(b.balance)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Payment Mode *</Label>
              <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} data-testid="payment-mode-select" className={selCls}>
                <option value="">Select mode…</option>
                {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Against GE-0001" data-testid="payment-note-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <Button type="submit" disabled={busy} data-testid="payment-save-btn"
              className={`w-full font-semibold transition-colors duration-200 ${form.kind === "receipt" ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950" : "bg-red-500 hover:bg-red-400 text-white"}`}>
              {busy ? "Saving…" : editId ? "Save Changes" : form.kind === "receipt" ? "Save Receipt" : "Save Voucher"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
